import { useState, useEffect, useRef } from "react";
import parse from "html-react-parser";
// @ts-ignore
import config from "config";
import apiRequest from "utils/ApiRequest/apiRequest";
import Image from "components/Elements/Image/Image";
import Icon from "components/Elements/Icon/Icon";
import SimpleContent from "components/Structure/ModalManager/SimpleContent.js";
import Login from "components/Structure/Login/Login.js";
import LimitSizeModalBlackFridayPromo from "./LimitSizeModalBlackFridayPromo";
import Form from "components/Reusable/Form/Form";
import Button, { ButtonA } from "components/Elements/Button/Button";
import { useContent } from "contexts/contentContext/contentContext";
import { useUser } from "contexts/userContext/userContext";
import { useModal } from "contexts/modalContext/modalContext";
import { LinkToNewTab } from "components/Elements/Link/Link";

import styles from "./limitsize.module.scss";

const errorsENUM = {
  uploadcodeinvalid: "upload-code-invalid",
  uploadcodenotexists: "upload-code-not-exist",
  uploadcodedused: "upload-code-used",
  uploadcodedcanceled: "upload-code-canceled",
  uploadcodedexpired: "upload-code-expired",
  default: "upload-code-invalid",
};

interface LimitSizeModalI {
  size: number;
  uploadStart: (code: string) => void;
  isSaved: boolean;
  setConfirmCode: (code: string) => void;
  userEmail: string;
}

export const LimitSizeModal = ({
  size,
  uploadStart,
  isSaved,
  setConfirmCode,
  userEmail,
}: LimitSizeModalI) => {
  const {
    getTextByKey,
    getPath,
    getHtmlByKey,
    getBlackFridayTime,
    getIsBlackFriday,
  } = useContent();
  const { isSubscribed, isLoggedIn, setUser, isBusiness } = useUser();
  const { closeModal, setModal } = useModal();

  const [countEnterCode, setCountEnterCode] = useState(0);
  const [openCode, setOpenCode] = useState(false);

  const codeSizeLimit = 21474836480; // 20GB

  const blackFridayModalCondition = getBlackFridayTime() && getIsBlackFriday();

  const cancelFrequentPromotion = useRef(false);
  const isUserFreqent = useRef(false);

  const handleAcceptFrequentPromotion = async ({
    loginCallback,
  }: {
    loginCallback: boolean;
  }) => {
    if (!isLoggedIn() && !loginCallback) {
      setModal({
        modal: Login,
        modalSize: "full-modal",
        props: {
          email: userEmail,
          submitCallback: () => {
            handleAcceptFrequentPromotion({ loginCallback: true });
          },
        },
      });
    } else {
      const response = await apiRequest(
        "Users",
        "SetTrialUser",
        { userEmail },
        "POST"
      );
      if (response.status === "success") {
        cancelFrequentPromotion.current = true;
        setUser(response.data);
        setModal({
          modal: SimpleContent,
          props: {
            image: {
              name: "purchases/present",
              ext: "png",
            },
            title: "succeed-trial-period-title",
            message: parse(
              getTextByKey("succeed-trial-period-message").replace(
                "#link#",
                `${config.baseurl}/l${getPath()}subscribers`
              )
            ),
            actionButton: (
              <Button className="new-button" onClick={closeModal}>
                {getTextByKey("succeed-trial-period-button")}
              </Button>
            ),
          },
        });
      }
    }
  };

  const getIsFrequentUser = async () => {
    const response = await apiRequest("Users", "ShouldGetTrialPro", {
      userEmail,
    });
    if (response.status === "success") {
      isUserFreqent.current = response.data;
    } else {
      isUserFreqent.current = false;
    }
  };

  const handleSubmit = async (body: {
    JumboCode: string;
    recaptchaResponse: string;
  }) => {
    setCountEnterCode((prev) => prev + 1);

    const response = await apiRequest(
      "UploadCodes",
      "ReactValidateUploadCodeBeforeUpload",
      {
        CodeKey: body.JumboCode,
        FilesTotalSize: size,
        RecaptchaResponse: body.recaptchaResponse,
        V3Recatcha: false,
      },
      "POST"
    );

    if (response.status === "success") {
      cancelFrequentPromotion.current = true;
      if (response.data === "UploadCodeConfirmed") {
        if (setConfirmCode) {
          setConfirmCode(body.JumboCode);
        }
        uploadStart(body.JumboCode);
        closeModal();
        document.querySelector(".custom-send-button").style.display = "none";
        return { status: "success" };
      } else {
        const message =
          errorsENUM[response.data.toLowerCase()] || errorsENUM.default;
        return {
          message: getTextByKey(message),
          status: "error",
        };
      }
    } else {
      return {
        message: errorsENUM.default,
        status: "error",
      };
    }
  };

  useEffect(() => {
    // on mount check if user should get trial pro
    getIsFrequentUser();

    return () => {
      if (
        !isUserFreqent.current ||
        cancelFrequentPromotion.current ||
        isSubscribed()
      )
        return;
      setTimeout(() => {
        setModal({
          modal: SimpleContent,
          props: {
            image: {
              name: "purchases/present",
              ext: "png",
            },
            title: "frequent-promotion-title",
            message: "frequent-promotion-message",
            additionalText: "frequent-promotion-additional-text",
            actionButton: (
              <Button
                className="new-button"
                onClick={handleAcceptFrequentPromotion}
              >
                {getTextByKey("frequent-promotion-button")}
              </Button>
            ),
          },
        });
      }, 100);
    };
  }, []);

  useEffect(() => {
    if (countEnterCode > 5) {
      setCountEnterCode(0);
    }
  }, [countEnterCode]);

  return (
    <div
      className={`${styles.wrapper} ${
        blackFridayModalCondition
          ? styles.blackFriday + " black-friday-counter"
          : styles.default
      }`}
    >
      {blackFridayModalCondition ? (
        <LimitSizeModalBlackFridayPromo />
      ) : (
        <>
          {!openCode ? (
            <div className={styles.limitSection}>
              <div className={styles.header}>
                <Image name="cloud_success" className={styles.cloudIcon} />
                <Image name="limited_image" className={styles.icon} />
                <Image
                  name="cloud"
                  ext="png"
                  className={styles.cloudImageLeft}
                />
                <Image
                  name="cloud"
                  ext="png"
                  className={styles.cloudImageRight}
                />
              </div>
              {getHtmlByKey(
                `limit-user-${
                  isSubscribed()
                    ? isBusiness()
                      ? "business"
                      : "pro"
                    : size < codeSizeLimit
                    ? "anon"
                    : "anon-sub-only"
                }`
              )}
              <ButtonA
                page="plans"
                title={getTextByKey("upgrade-plan")}
                target="_blank"
              />
              {isSaved && isSubscribed() && (
                <ButtonA
                  page="inbox/manageStorage?t=all&sm=saved"
                  onClick={() => {
                    closeModal();
                  }}
                  target="_blank"
                  className={styles.blank}
                  title={getTextByKey("not-enough-space-remove-items")}
                  blank
                />
              )}
              {!isSubscribed() && size < codeSizeLimit ? (
                <button
                  className={styles.jumbocodeTrigger}
                  onClick={() => {
                    setOpenCode((prev) => !prev);
                  }}
                >
                  {getTextByKey("already-have-jumbocode-text")}
                </button>
              ) : null}
            </div>
          ) : (
            <div className={styles.codeSection}>
              <h3>{getTextByKey("jumbocode-popup-title")}</h3>
              <Form
                fields={{
                  JumboCode: {
                    fieldKey: "JumboCode",
                    element: "input",
                    type: "text",
                    title: "jubmocode",
                    label: "save-code-placeholder",
                    placeholder: "",
                    validation: {
                      required: true,
                      reg: /[^\n]+/,
                      invalidMessage: "default-input-invalid-message",
                    },
                  },
                }}
                onSubmit={handleSubmit}
                submitProps={{
                  title: getTextByKey("upload-send"),
                }}
              />
              <div className={styles.upgradePlan}>
                {getTextByKey("or")}
                <LinkToNewTab page="plans" isNewTab>
                  <Icon name="jm-marketing-icon" />
                  {getTextByKey("upgrade-plan")}
                </LinkToNewTab>
              </div>

              <div className={styles.footer}>
                <Icon name="jumbomail-icon" ext="png" iconClass={styles.icon} />
                <Image
                  name="cloud"
                  ext="png"
                  className={styles.cloudImageLeft}
                />
                <Image
                  name="cloud"
                  ext="png"
                  className={styles.cloudImageRight}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
